import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Plus, Receipt, SearchIcon } from "@/components/icons";
import {
  Badge,
  Button,
  Input,
  Skeleton,
  useDebounceCallback,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useAddressOptionsQuery } from "@/modules/customers/presentation/api/queries";
import type { OrderStatus } from "@/modules/orders/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useOrdersQuery } from "../api/queries";
import { OrdersListPagination } from "../ui/OrdersListPagination";
import {
  DELIVERY_METHOD_META,
  getOrderStatusMeta,
  PAYMENT_TYPE_META,
  orderStatusLabel,
} from "../ui/order-status";

const STATUS_TABS: Array<{ value: "all" | OrderStatus; label: string }> = [
  { value: "all", label: "ທັງໝົດ" },
  { value: "draft", label: "ຮ່າງ" },
  { value: "paid", label: "ຢືນຢັນ / ຊຳລະແລ້ວ" },
  { value: "shipped", label: "ສົ່ງແລ້ວ" },
  { value: "delivered", label: "ຂົນສົ່ງສົ່ງຮອດ" },
  { value: "picked_up", label: "ລູກຄ້າໄປເອົາ" },
  { value: "returned", label: "ຂົນສົ່ງຕີກັບ" },
  { value: "cancelled", label: "ຍົກເລີກ" },
];

const DEFAULT_LIMIT = 20;
const ALL_VALUE = "__all__";

export function OrdersListPage() {
  const nav = useNavigate();
  const canManage = useActionPermission(["orders:write"]);
  const [status, setStatus] = useState<"all" | OrderStatus>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [province, setProvince] = useState("");
  const [district, setDistrict] = useState("");
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  const debouncedSearch = useDebounceCallback((v: string) => setSearch(v), 400);

  useEffect(() => {
    setOffset(0);
  }, [status, search, province, district]);

  const list = useOrdersQuery({
    status: status === "all" ? undefined : status,
    search: search || undefined,
    province: province || undefined,
    district: district || undefined,
    offset,
    limit,
  });
  const orders = list.data?.data ?? [];
  const totalCount = list.data?.meta?.total ?? 0;

  const provinces = useAddressOptionsQuery({ level: "province" });
  const districts = useAddressOptionsQuery(
    { level: "district", province },
    Boolean(province),
  );

  const provinceOptions = useMemo(
    () => [
      { value: ALL_VALUE, label: "ທຸກແຂວງ" },
      ...(provinces.data?.data ?? []).map((row) => ({
        value: row.value,
        label: `${row.value} (${row.count})`,
      })),
    ],
    [provinces.data?.data],
  );

  const districtOptions = useMemo(
    () => [
      { value: ALL_VALUE, label: "ທຸກເມືອງ" },
      ...(districts.data?.data ?? []).map((row) => ({
        value: row.value,
        label: `${row.value} (${row.count})`,
      })),
    ],
    [districts.data?.data],
  );

  const hasFilters =
    Boolean(search) ||
    status !== "all" ||
    Boolean(province) ||
    Boolean(district);

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ອໍເດີ້
            </h2>
            <p className="text-muted-foreground text-sm">
              ຈັດການອໍເດີ້ ແລະ ກອງຕາມແຂວງ/ເມືອງ
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          {canManage ? (
            <Button
              className="shrink-0 rounded-full"
              onClick={() => nav({ to: "/app/orders/new" })}
            >
              <Plus data-icon="inline-start" />
              ສ້າງອໍເດີ້ດ່ວນ
            </Button>
          ) : null}
        </div>

        <div className="flex flex-col gap-3">
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                debouncedSearch(e.target.value);
              }}
              placeholder="ຄົ້ນຫາ ຊື່ / ເບີໂທ / ເລກອໍເດີ້..."
              className="pl-9"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <SimpleSelect
              value={province || ALL_VALUE}
              onValueChange={(value) => {
                const next = value === ALL_VALUE ? "" : value;
                setProvince(next);
                setDistrict("");
              }}
              options={provinceOptions}
              placeholder="ແຂວງ"
            />
            <SimpleSelect
              value={district || ALL_VALUE}
              onValueChange={(value) =>
                setDistrict(value === ALL_VALUE ? "" : value)
              }
              options={districtOptions}
              placeholder="ເມືອງ"
              disabled={!province}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setStatus(tab.value)}
                className={cn(
                  "rounded-full border px-4 py-1.5 text-sm transition-colors",
                  status === tab.value
                    ? "border-secondary bg-secondary text-secondary-foreground"
                    : "border-secondary/30 bg-background hover:bg-secondary/10",
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {list.isLoading ? (
          <div className="flex flex-col gap-3">
            {["one", "two", "three", "four", "five"].map((id) => (
              <Skeleton key={id} className="h-20 rounded-xl" />
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-secondary/20 border-dashed py-16 text-center">
            <Receipt className="size-10 text-muted-foreground/50" />
            <p className="text-muted-foreground">
              {hasFilters ? "ບໍ່ພົບອໍເດີ້ຕາມການຄົ້ນຫາ" : "ຍັງບໍ່ມີອໍເດີ້"}
            </p>
            {canManage && !hasFilters ? (
              <Button
                className="rounded-full"
                onClick={() => nav({ to: "/app/orders/new" })}
              >
                <Plus data-icon="inline-start" />
                ສ້າງອໍເດີ້ທຳອິດ
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {orders.map((order) => {
              const meta = getOrderStatusMeta(order.status, order.paymentType);
              return (
                <button
                  key={order.id}
                  type="button"
                  onClick={() =>
                    nav({ to: "/app/orders/$id", params: { id: order.id } })
                  }
                  className="brand-card flex items-center gap-4 p-4 text-left transition-shadow hover:shadow-md"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display text-foreground">
                        {order.customerName}
                      </span>
                      <Badge variant={meta?.variant ?? "outline"}>
                        {orderStatusLabel(order.status, order.paymentType)}
                      </Badge>
                      <Badge
                        variant={PAYMENT_TYPE_META[order.paymentType].variant}
                      >
                        {PAYMENT_TYPE_META[order.paymentType].shortLabel}
                      </Badge>
                      <Badge
                        variant={
                          DELIVERY_METHOD_META[order.deliveryMethod].variant
                        }
                      >
                        {DELIVERY_METHOD_META[order.deliveryMethod].shortLabel}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-x-3 text-muted-foreground text-xs">
                      <span className="font-mono">{order.orderNumber}</span>
                      {order.customerPhone ? (
                        <span>{order.customerPhone}</span>
                      ) : null}
                      <span>{order.itemCount} ລາຍການ</span>
                      {order.deliveryMethod === "carrier" && order.shippingBranch
                        ? (
                          <span>ສາຂາ {order.shippingBranch}</span>
                        )
                        : null}
                      <span>{formatDateTimeLocal(order.createdAt)}</span>
                    </div>
                  </div>
                  <span className="shrink-0 font-display text-foreground text-lg tabular-nums">
                    {formatLak(order.total)}
                  </span>
                </button>
              );
            })}
            <OrdersListPagination
              offset={offset}
              limit={limit}
              totalCount={totalCount}
              isLoading={list.isFetching}
              onChange={(nextOffset, nextLimit) => {
                setOffset(nextOffset);
                setLimit(nextLimit);
              }}
            />
          </div>
        )}
      </Main>
    </>
  );
}
